import React, { useEffect, useRef, useState } from "react"
import classNames from "classnames"

import { Labels } from "../../../util/Translations"
import { useAppSelector } from "../../../hooks"
import Separator from "../../common/Separator"

import { checkIfContainerIsScrolledToBottom } from "../utils/ScrollUtils"
import { selectAIModalById } from "../selectors"
import { IId } from ".."

import Messages from "./Messages"
import Inputs from "./Inputs"

export type Props = IId

export default function AIModalContent({ id }: Props): JSX.Element {
    const AIModalData = useAppSelector((state) => selectAIModalById(state, id))
    if (!AIModalData) {
        return <></>
    }

    const { AiAssistance } = Labels

    const scrollRef = useRef<HTMLDivElement | null>(null)
    const [isScrolledToBottom, setIsAtBottom] = useState(true)

    const handleScroll = (): void => {
        setIsAtBottom(checkIfContainerIsScrolledToBottom(scrollRef))
    }

    // because of the specific scrolling on button press
    // we cannot use the sticky footer
    // so we have to calculate the shadow manually
    useEffect(() => {
        const element = scrollRef.current
        if (!element) {
            return
        }

        // initial shadow check in case of closing and reopening of modal
        handleScroll()

        // and also on scrolling we have to always ensure the shadow's visibility
        element.addEventListener("scroll", handleScroll)

        // and on window resize as well
        window.addEventListener("resize", handleScroll)

        return () => {
            element.removeEventListener("scroll", handleScroll)
            window.removeEventListener("resize", handleScroll)
        }
    }, [])

    // we reuse the sticky design from the resource footer,
    // hence why the classname below is NewUI__footer__container
    // this is the same approach in all sticky footers
    const stickyClass = classNames({
        sticky: !isScrolledToBottom,
    })

    return (
        <div className="tw-flex tw-flex-col tw-pb-2">
            <div
                className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto has-hidden-scrollbar"
                data-ft-component="scrollarea"
                ref={scrollRef}
            >
                <div className="tw-flex tw-flex-col">
                    <header className="step-header">
                        <h3 className="tw-mx-2">{AiAssistance}</h3>
                    </header>

                    <Separator />

                    <Messages id={id} scrollRef={scrollRef} />
                </div>
            </div>

            <div className="NewUI__footer__container">
                <div className={stickyClass}>
                    {isScrolledToBottom && <Separator />}
                    <Inputs id={id} />
                </div>
            </div>
        </div>
    )
}
